import { ApiClient, apiClient } from './apiClient';
import { API_ENDPOINTS, QUERY_ACTIONS_BASE_URL } from '../config/api';
import { METADATA_AGENT_ID } from '../config/agentApi';
import { agentService } from './agentService';
import {
  QueryDefinition,
  ExecuteQueryRequest,
  QueryExecutionResponse,
  ValidateQueryRequest,
  ValidationResponse,
  ScheduleQueryRequest,
  ScheduleQueryResponse,
  OptimizeSqlRequest,
  OptimizeSqlResponse,
  GenerateSqlRequest,
  GenerateSqlResponse,
  GenerateMetadataRequest,
  GenerateMetadataResponse,
  QuerySearchRequest,
  ResultTable,
  QueryVersion,
  OverrideVersion,
  TerminalScheduleConfig,
  PageResponse,
  Application,
  TerminalOverride,
  TerminalOverrideRequest,
} from '../types';

const queryActionsApiClient = new ApiClient(QUERY_ACTIONS_BASE_URL);

const normalizeQueryDefinition = (query: QueryDefinition): QueryDefinition => {
  if (!query) {
    return query;
  }
  const activeFlag = query.active ?? query.isActive ?? false;
  return {
    ...query,
    active: activeFlag,
    isActive: activeFlag,
  };
};

const extractQueryList = (
  response: PageResponse<QueryDefinition> | QueryDefinition[] | undefined
): QueryDefinition[] => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response.map(normalizeQueryDefinition);
  }
  if (Array.isArray(response.content)) {
    return response.content.map(normalizeQueryDefinition);
  }
  return [];
};

export const queryService = {
  // Query Management
  async listQueries(
    activeOnly: boolean = true,
    options: { page?: number; size?: number; sort?: string } = {}
  ): Promise<QueryDefinition[]> {
    const params = {
      activeOnly,
      page: options.page ?? 0,
      size: options.size ?? 200,
      sort: options.sort,
    };
    const response = await apiClient.get<PageResponse<QueryDefinition> | QueryDefinition[]>(
      API_ENDPOINTS.queries,
      { params }
    );

    return extractQueryList(response);
  },

  async getCategoryCounts(request?: Partial<QuerySearchRequest>): Promise<Record<string, number>> {
    return apiClient.post(API_ENDPOINTS.categoryCounts, {
      activeOnly: request?.activeOnly,
      searchTerm: request?.searchTerm,
      tags: request?.tags,
      applicationId: request?.applicationId,
    });
  },

  async searchQueries(request: QuerySearchRequest): Promise<PageResponse<QueryDefinition>> {
    const response = await apiClient.post<PageResponse<QueryDefinition>>(
      API_ENDPOINTS.querySearch,
      request
    );
    // Ensure content is normalized
    if (response && Array.isArray(response.content)) {
      response.content = response.content.map(normalizeQueryDefinition);
    }
    return response;
  },

  async getQuery(queryId: string): Promise<QueryDefinition> {
    const response = await apiClient.get<QueryDefinition>(API_ENDPOINTS.queryById(queryId));
    return normalizeQueryDefinition(response);
  },

  async createQuery(query: Partial<QueryDefinition>): Promise<QueryDefinition> {
    const response = await queryActionsApiClient.post<QueryDefinition>(API_ENDPOINTS.queries, query);
    return normalizeQueryDefinition(response);
  },

  async updateQuery(queryId: string, query: Partial<QueryDefinition>): Promise<QueryDefinition> {
    console.log('[QueryService] updateQuery payload:', query);
    const response = await queryActionsApiClient.put<QueryDefinition>(API_ENDPOINTS.queryById(queryId), query);
    return normalizeQueryDefinition(response);
  },

  async setQueryActivation(
    queryId: string,
    active: boolean,
    updatedBy?: string
  ): Promise<QueryDefinition> {
    const response = await apiClient.post<QueryDefinition>(API_ENDPOINTS.queryActivation(queryId), {
      active,
      updatedBy,
    });
    return normalizeQueryDefinition(response);
  },

  async deleteQuery(queryId: string): Promise<void> {
    return apiClient.delete(API_ENDPOINTS.queryById(queryId));
  },

  async createVersion(queryId: string, version: Partial<QueryVersion>): Promise<QueryVersion> {
    return apiClient.post(API_ENDPOINTS.queryVersions(queryId), version);
  },

  async getVersionHistory(queryId: string, limit?: number): Promise<QueryVersion[]> {
    return apiClient.get(API_ENDPOINTS.queryVersionHistory(queryId), {
      params: { limit }
    });
  },

  async setCurrentVersion(queryId: string, versionNumber: string): Promise<void> {
    return apiClient.post(API_ENDPOINTS.queryVersionSetCurrent(queryId, versionNumber));
  },

  // Query Execution
  async executeQuery(queryId: string, request: ExecuteQueryRequest): Promise<QueryExecutionResponse> {
    return queryActionsApiClient.post(API_ENDPOINTS.queryExecute(queryId), request);
  },

  async executeQueryVersion(queryId: string, version: string, request: ExecuteQueryRequest): Promise<QueryExecutionResponse> {
    return queryActionsApiClient.post(API_ENDPOINTS.queryExecuteVersion(queryId, version), request);
  },

  async executeRawSql(request: {
    sqlContent: string;
    terminalId: string;
    databaseType?: string;
    connectionId?: string;
    parameters?: Record<string, any>;
    parameterTypes?: Record<string, string>;
    applicationId?: string;
  }): Promise<any> {
    // Call the new backend proxy endpoint which handles authentication and routing to DB service
    const url = '/queries/test';

    const payload: Record<string, any> = {
      queryId: 'test-execution-' + Date.now(),
      terminalId: request.terminalId,
      databaseType: request.databaseType || 'ORACLE',
      sqlContent: request.sqlContent,
      parameters: request.parameters || {},
      returnResults: true,
    };

    if (request.parameterTypes && Object.keys(request.parameterTypes).length > 0) {
      payload.parameterTypes = request.parameterTypes;
    }

    if (request.applicationId) {
      payload.applicationId = request.applicationId;
    }

    if (request.connectionId) {
      payload.connectionId = request.connectionId;
    }

    return queryActionsApiClient.post(url, payload);
  },

  // Query Validation
  async validateQuery(request: ValidateQueryRequest): Promise<ValidationResponse> {
    return queryActionsApiClient.post(API_ENDPOINTS.validate, request);
  },

  // Query Scheduling
  // Query Scheduling
  async checkScheduleAvailability(
    name: string,
    type: 'SCHEDULE_NAME' | 'RESULT_TABLE_NAME',
    excludeScheduleId?: string
  ): Promise<{ available: boolean; message?: string }> {
    return apiClient.get(API_ENDPOINTS.checkScheduleAvailability, {
      params: { name, type, excludeScheduleId },
    });
  },

  async scheduleQuery(request: ScheduleQueryRequest, userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone): Promise<ScheduleQueryResponse> {
    return apiClient.post(API_ENDPOINTS.schedule, request, {
      headers: { 'X-User-Timezone': userTimezone }
    });
  },

  async listSchedules(userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone): Promise<ScheduleQueryResponse[]> {
    return apiClient.get(API_ENDPOINTS.schedules, {
      headers: { 'X-User-Timezone': userTimezone }
    });
  },

  async getSchedule(scheduleId: string, userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone): Promise<ScheduleQueryResponse> {
    return apiClient.get(API_ENDPOINTS.scheduleById(scheduleId), {
      headers: { 'X-User-Timezone': userTimezone }
    });
  },

  async updateSchedule(scheduleId: string, request: Partial<ScheduleQueryRequest>, userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone): Promise<ScheduleQueryResponse> {
    return apiClient.put(API_ENDPOINTS.scheduleById(scheduleId), request, {
      headers: { 'X-User-Timezone': userTimezone }
    });
  },

  async deleteSchedule(scheduleId: string): Promise<void> {
    return apiClient.delete(API_ENDPOINTS.scheduleById(scheduleId));
  },
  async listTerminalConfigs(): Promise<TerminalScheduleConfig[]> {
    return apiClient.get(API_ENDPOINTS.terminalConfigs);
  },

  async getTerminalConfig(terminalId: string): Promise<TerminalScheduleConfig> {
    return apiClient.get(API_ENDPOINTS.terminalConfigById(terminalId));
  },

  // AI Features
  async optimizeQuery(request: OptimizeSqlRequest): Promise<OptimizeSqlResponse> {
    return apiClient.post(API_ENDPOINTS.optimize, request);
  },

  async generateSql(request: GenerateSqlRequest): Promise<GenerateSqlResponse> {
    return apiClient.post(API_ENDPOINTS.generate, request);
  },

  async generateMetadata(request: GenerateMetadataRequest): Promise<GenerateMetadataResponse> {
    const executionResult = await agentService.execute(METADATA_AGENT_ID, request.sqlContent);

    try {
      // The agent response is a JSON string describing the metadata
      const rawResponse = executionResult.response;
      console.log('Agent raw response:', rawResponse);
      const jsonStr = rawResponse.replace(/```json/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(jsonStr);
      console.log('Parsed metadata:', parsed);
      return {
        queryId: parsed.queryId || parsed.query_id,
        name: parsed.name,
        description: parsed.description,
        category: parsed.category,
        tags: parsed.tags || [],
      };
    } catch (e) {
      console.error('Failed to parse agent response as JSON', e, executionResult.response);
      // If parsing fails, try to extract basic info
      return {
        name: 'Generated Query',
        description: 'Unable to auto-generate. Please fill manually.',
        category: 'System',
        tags: [],
      };
    }
  },

  async explainPlanWithAi(sqlContent: string, planLines: string[], modelName?: string): Promise<string> {
    const DOMAIN_EXPERT_AGENT_ID = 'a1a20000-0000-0000-0000-000000000005';
    const planText = planLines.join('\n');

    // Formatting/output rules are owned by the agent's backend business_rules (V32 migration).
    // The UI sends only raw data — the agent's system prompt handles structure and constraints.
    const prompt = `Analyze the execution plan for this SQL query.\n\nSQL:\n\`\`\`sql\n${sqlContent}\n\`\`\`\n\nExecution Plan:\n\`\`\`text\n${planText}\n\`\`\``;

    const result = await agentService.execute(DOMAIN_EXPERT_AGENT_ID, prompt, 'web_ui', undefined, undefined, modelName);
    return result.response;
  },

  // Results
  async listResultTables(): Promise<ResultTable[]> {
    return apiClient.get(API_ENDPOINTS.results);
  },

  async getResults(
    tableName: string,
    page: number = 0,
    size: number = 100,
    terminalId?: string
  ): Promise<any> {
    return apiClient.get(API_ENDPOINTS.resultsByTable(tableName), {
      params: { page, size, terminalId },
    });
  },

  // Health Check
  async checkHealth(): Promise<any> {
    return apiClient.get(API_ENDPOINTS.health);
  },

  // Schema Exploration
  async exploreSchema(terminalId: string, tableName?: string, databaseType: string = 'ORACLE'): Promise<any[]> {
    return apiClient.get(API_ENDPOINTS.schemaExplore, {
      params: { terminalId, tableName, databaseType }
    });
  },

  // Favorites
  async toggleFavorite(queryId: string, isFavorite: boolean): Promise<QueryDefinition> {
    const response = await apiClient.post<QueryDefinition>(`/queries/${queryId}/favorite`, { isFavorite });
    return normalizeQueryDefinition(response);
  },

  async getUserFavorites(): Promise<string[]> {
    return apiClient.get<string[]>('/queries/favorites');
  },

  async getFavoriteStatus(queryId: string): Promise<{ isFavorite: boolean }> {
    return apiClient.get<{ isFavorite: boolean }>(`/queries/${queryId}/favorite/status`);
  },

  // Tags
  async getAllTags(applicationId?: string): Promise<string[]> {
    const params = applicationId ? { applicationId } : undefined;
    return apiClient.get<string[]>('/queries/tags', { params });
  },

  async addTags(queryId: string, tags: string[]): Promise<void> {
    return apiClient.post(`/queries/${queryId}/tags`, { tags });
  },

  async removeTags(queryId: string, tags: string[]): Promise<void> {
    return apiClient.delete(`/queries/${queryId}/tags`, { data: { tags } });
  },

  // Terminal Overrides
  async listTerminalOverrides(queryId: string): Promise<TerminalOverride[]> {
    return apiClient.get<TerminalOverride[]>(`/queries/${queryId}/overrides`);
  },

  async getTerminalOverride(queryId: string, terminalId: string): Promise<TerminalOverride | null> {
    try {
      return await apiClient.get<TerminalOverride>(`/queries/${queryId}/overrides/${terminalId}`);
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async saveTerminalOverride(
    queryId: string,
    terminalId: string,
    request: TerminalOverrideRequest,
  ): Promise<TerminalOverride> {
    return apiClient.post<TerminalOverride>(`/queries/${queryId}/overrides/${terminalId}`, request);
  },

  async deleteTerminalOverride(queryId: string, terminalId: string): Promise<void> {
    return apiClient.delete(`/queries/${queryId}/overrides/${terminalId}`);
  },

  async getOverrideVersionHistory(queryId: string, terminalId: string): Promise<OverrideVersion[]> {
    return apiClient.get<OverrideVersion[]>(`/queries/${queryId}/overrides/${terminalId}/versions`);
  },

  async setCurrentOverrideVersion(queryId: string, terminalId: string, versionNumber: number): Promise<void> {
    await apiClient.post(`/queries/${queryId}/overrides/${terminalId}/versions/${versionNumber}/set-current`, {});
  },

  // Reference Data
  async getApplications(): Promise<Application[]> {
    return apiClient.get(API_ENDPOINTS.applications);
  },
};

export default queryService;
